import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { CourseOption, CourseSummary } from "../types/course.response";
import { Requirement } from "../types/requirement.response";
import { getSuggestedCourseIds } from "../services/requirementSuggestions";
import {
    ensureHydrated,
    getCatalogEntry,
    getCatalogVersion,
    subscribeCatalog,
} from "../services/courseCatalog";
import { INITIAL_USER_STATE } from "../utils/fulfillmentMerge";
import { getBroadKind } from "../utils/requirementResponseUtils";
import { toCourseSummary } from "../utils/programData";

export type SuggestionsStatus = "idle" | "loading" | "loaded" | "error";

/**
 * Broad(内涵)requirement 的推荐课:suggestions 端点拿 courseIds(会话级缓存),
 * 经 /courses/batch 补水后合成"未在用户课表"初始态的 CourseSummary 行。
 * 非内涵 requirement 恒为 idle、零请求。后端已排除请求时刻的用户已有课;
 * 会话内新增的课按最新 userCourses 在这里再过滤(含 topic 变体,按课号比)。
 */
export function useSuggestedCourses(
    programId: string,
    requirement: Requirement,
    userCourses?: CourseOption[],
): { status: SuggestionsStatus; courses: CourseSummary[] } {
    const enabled = getBroadKind(requirement) === "intensional";
    const requirementId = requirement.info.id;

    const [status, setStatus] = useState<SuggestionsStatus>(enabled ? "loading" : "idle");
    const [courseIds, setCourseIds] = useState<string[]>([]);
    const catalogVersion = useSyncExternalStore(subscribeCatalog, getCatalogVersion);

    useEffect(() => {
        if (!enabled) return;
        let cancelled = false;
        setStatus("loading");
        setCourseIds([]);
        getSuggestedCourseIds(programId, requirementId)
            .then(async ids => {
                if (ids.length > 0) await ensureHydrated(ids);
                if (cancelled) return;
                setCourseIds(ids);
                setStatus("loaded");
            })
            .catch(err => {
                console.error(
                    `[useSuggestedCourses] failed (program=${programId}, requirement=${requirementId})`,
                    err,
                );
                if (!cancelled) setStatus("error");
            });
        return () => { cancelled = true; };
    }, [enabled, programId, requirementId]);

    const courses = useMemo(() => {
        void catalogVersion; // catalog 缓存变化时重新合成
        if (courseIds.length === 0) return [];
        const ownedIds = new Set((userCourses ?? []).map(c => c.id));
        const result: CourseSummary[] = [];
        for (const id of courseIds) {
            if (ownedIds.has(id)) continue;
            const catalog = getCatalogEntry(id);
            if (!catalog) continue; // 补水未到/被清,该行先不出
            result.push(
                toCourseSummary({ ...catalog, userItemId: null, userState: INITIAL_USER_STATE }, id),
            );
        }
        return result;
    }, [courseIds, userCourses, catalogVersion]);

    return { status, courses };
}
